import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { useAuth, useCart } from "../context/AppContext";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/accessories", label: "Accessories" },
  { to: "/science", label: "Science" },
  { to: "/testing", label: "Testing" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-nav"
        className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500 bg-neutral-900/55 backdrop-blur-md border-b border-white/8"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/assets/emblem.png" alt="BIO LABZ" className="h-9 lg:h-10 w-auto" />
            <div className="hidden sm:block leading-none">
              <div className="font-display text-white text-sm tracking-[0.28em]">BIO LABZ</div>
              <div className="font-mono text-[9px] text-white/40 tracking-[0.35em] mt-1">REGENERATION</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-2 p-1 border border-white/8">
            {LINKS.map(l => (
              <NavLink
                key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 font-mono text-[11px] tracking-[0.25em] uppercase transition-all ${
                    isActive
                      ? "bg-[color:var(--bl-blue)] text-black"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to={user ? "/account" : "/login"} data-testid="nav-account"
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-[0.28em] uppercase text-white/70 hover:text-white transition-colors">
              <User size={15} />
              <span>{user ? user.full_name.split(" ")[0] : "Member"}</span>
            </Link>
            <button
              data-testid="nav-cart-btn"
              onClick={() => setCartOpen(true)}
              className="relative inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] tracking-[0.28em] uppercase text-white/80 hover:text-white transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span className="ml-1 min-w-[22px] h-[22px] px-1 rounded-full bg-[color:var(--bl-blue)] text-black text-[10px] flex items-center justify-center font-semibold">
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden text-white p-2"
              onClick={() => setOpen(v => !v)}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="fixed inset-0 top-16 z-40 bg-black/95 backdrop-blur-xl lg:hidden">
          <div className="px-6 py-8 flex flex-col gap-1">
            {LINKS.map(l => (
              <NavLink
                key={l.to} to={l.to}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
                className="py-4 border-b border-white/5 font-display uppercase text-2xl tracking-tight text-white"
              >
                {l.label}
              </NavLink>
            ))}
            <Link to={user ? "/account" : "/login"} className="py-4 border-b border-white/5 font-display uppercase text-2xl tracking-tight text-white">
              {user ? "Account" : "Sign In"}
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
